import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

function Tabs({ className, orientation = 'horizontal', ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  'group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-0.75 text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        line: 'bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function TabsList({
  className,
  variant = 'default',
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        // -- base layout & typography --
        'relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center',
        'gap-1.5 rounded-md border border-transparent px-1.5 py-0.5',
        'text-sm font-medium whitespace-nowrap transition-all',
        'group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
        'has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1',
        'transition-all',

        // -- default state --
        'text-foreground/60 dark:text-muted-foreground',

        // -- hover state --
        'hover:text-foreground dark:hover:text-foreground',

        // -- focus-visible state --
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring',

        // -- disabled state --
        'disabled:pointer-events-none disabled:opacity-50',
        'aria-disabled:pointer-events-none aria-disabled:opacity-50',

        // -- active (selected) state: default variant --
        'group-data-[variant=default]/tabs-list:data-active:bg-background',
        'group-data-[variant=default]/tabs-list:data-active:text-foreground',
        'group-data-[variant=default]/tabs-list:data-active:shadow-sm',
        'dark:group-data-[variant=default]/tabs-list:data-active:border-input',
        'dark:group-data-[variant=default]/tabs-list:data-active:bg-input/30',
        'dark:group-data-[variant=default]/tabs-list:data-active:text-foreground',

        // -- line variant: base --
        'group-data-[variant=line]/tabs-list:h-8',
        'group-data-[variant=line]/tabs-list:text-ui-body-b1 group-data-[variant=line]/tabs-list:leading-ui-body-b1',
        'group-data-[variant=line]/tabs-list:bg-transparent',
        'group-data-[variant=line]/tabs-list:data-active:bg-transparent',
        'dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
        'dark:group-data-[variant=line]/tabs-list:data-active:border-transparent',
        'group-data-[variant=line]/tabs-list:data-active:shadow-none',

        // -- line variant: hover state --
        'group-data-[variant=line]/tabs-list:hover:text-creation-primary-container-on-surface-hover',

        // -- line variant: pressed state --
        'group-data-[variant=line]/tabs-list:active:text-creation-primary-container-on-surface-pressed',

        // -- line variant: active (selected) state --
        'group-data-[variant=line]/tabs-list:data-active:text-creation-primary-container-on-surface-focused',

        // -- indicator (::after) base --
        'after:absolute after:transition-all',
        'group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:-bottom-1.25 group-data-horizontal/tabs:after:h-0.5',
        'group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5',

        // -- indicator: line variant base (4px, rounded top, flush bottom) --
        'group-data-[variant=line]/tabs-list:after:h-1',
        'group-data-[variant=line]/tabs-list:after:rounded-t-100',
        'group-data-[variant=line]/tabs-list:group-data-horizontal/tabs:after:bottom-0',

        // -- indicator: line variant separator (inactive + not hovered + not pressed) --
        'group-data-[variant=line]/tabs-list:[&:not([data-active]):not(:hover):not(:active)]:after:h-px',
        'group-data-[variant=line]/tabs-list:[&:not([data-active]):not(:hover):not(:active)]:after:bg-main-border-lv03',

        // -- indicator: line variant hover state --
        'group-data-[variant=line]/tabs-list:hover:after:bg-creation-primary-solid-surface-hover',

        // -- indicator: line variant active (selected) state --
        'group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
        'group-data-[variant=line]/tabs-list:data-active:after:bg-creation-primary-solid-surface-focused',

        // -- indicator: line variant pressed state --
        'group-data-[variant=line]/tabs-list:active:after:bg-creation-primary-solid-surface-pressed',

        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 text-sm outline-none', className)} {...props} />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
